import { useCallback } from 'react';

// eslint-disable-next-line @nx/enforce-module-boundaries
import { useAppsFlyer } from '@ocx/cap-appsflyer';

import { useAlerts } from './useAlerts';
import { useIsOnline } from '../../hooks/useIsOnline';
import { useLoader } from '../../lib/loader/LoaderProvider';
import { useGetNavigationActions } from '../../hooks/useGetNavigationActions';
import { useIsPayAtPumpAvailable } from './useIsPayAtPumpAvailable';
import { useRudderStack } from '../../lib/rudderStack/useRudderStack';

export interface IUseStartPayPumpParams {
  isEmailCompleted: boolean;
}

export const useStartPayAtPump = (params: IUseStartPayPumpParams) => {
  const { triggerEvent } = useRudderStack();
  const { logEvent: logAppsFlyerEvent } = useAppsFlyer();

  const alerts = useAlerts();
  const isOnline = useIsOnline();
  const { turnLoader } = useLoader();
  const { pushToSelectPump } = useGetNavigationActions();
  const { isEmailCompleted } = params;
  const canPayAtPump = useIsPayAtPumpAvailable();

  return useCallback(async () => {
    triggerEvent('pay_at_pump_clicked');

    try {
      if (!isOnline) {
        alerts.showNoConnectionAlert();
        return;
      }

      turnLoader(true);
      const { isAvailable, noPumpsAvailable, notClose, noMobilePaymentEnabled, noGeolocation, storeId } =
        await canPayAtPump();
      turnLoader(false);

      if (!isAvailable) {
        if (notClose) {
          alerts.showNoStoreFoundAlert();
        } else if (noMobilePaymentEnabled || noPumpsAvailable) {
          alerts.showNoMobilePaymentAlert();
        } else if (noGeolocation) {
          alerts.showNoGeolocationAvailableAlert();
        }
        return;
      }

      if (storeId === null) {
        return;
      }

      if (!isEmailCompleted) {
        alerts.showCompleteEmailAlert({
          onCompleted: () => {
            // It was decided to hardcode the event name for Gulf until AppsFlyer
            // will be used by other clients. It should be changed to more generic name later.
            logAppsFlyerEvent({
              eventName: 'gp_email_address_entered',
            });
            pushToSelectPump(storeId);
          },
        });
        return;
      }

      pushToSelectPump(storeId);
    } finally {
      turnLoader(false);
    }
  }, [isEmailCompleted, isOnline, turnLoader, canPayAtPump, alerts, pushToSelectPump, triggerEvent, logAppsFlyerEvent]);
};
